/**
 * Camera state and coordinate math.
 *
 * Per MAP.md §8, the camera has 3 zoom steps (2.5 / 4 / 8 px per cell)
 * matching the prototype-validated middle zoom. Pan lives in world
 * coordinates (cells). Entity sprites are rendered screen-scaled — the
 * decoupled entity sizing rule from MAP.md §1 lives in the renderer,
 * not here.
 *
 * v0M2.4 pans via keyboard (arrows / WASD) and edge scroll. Mouse-drag
 * pan is intentionally excluded (see UI_UX.md §1 rule 1 discipline:
 * left-click selects, right-click orders; drag-pan would need a
 * dedicated modifier to avoid stealing from those semantics — deferred
 * to a later polish sub-checkpoint).
 */

/**
 * Zoom step lookup: pixels per cell.
 *
 * MFix5+fix7 (2026-07-23) — expanded from 3 steps (2.5, 4, 8) to 7
 * steps for finer control. Michael's playtest ask: more zoom levels.
 * Ends match the old bounds so no existing behaviour breaks; the new
 * mid-steps + one closer step (16) give ~2× finer granularity plus
 * closer inspection of tactical positioning near cities.
 *
 * `DEFAULT_ZOOM_STEP` re-indexed to keep the same default px/cell (4).
 */
export const ZOOM_STEPS: readonly number[] = [1.5, 2.5, 3.5, 4, 5.5, 8, 12, 16];

/**
 * Default zoom step. MFix5+fix7 re-indexed to `3` so the default px/cell
 * remains 4 (unchanged from the pre-expansion default).
 */
export const DEFAULT_ZOOM_STEP = 3;

export interface Camera {
  /** World-space camera centre X in cell units. */
  centerX: number;
  /** World-space camera centre Y in cell units. */
  centerY: number;
  /** Index into ZOOM_STEPS. */
  zoomStep: number;
}

export function createCamera(mapWidth: number, mapHeight: number): Camera {
  return {
    centerX: mapWidth / 2,
    centerY: mapHeight / 2,
    zoomStep: DEFAULT_ZOOM_STEP,
  };
}

/** Effective zoom (px per world cell) for the current camera. */
export function pxPerCell(camera: Camera): number {
  return ZOOM_STEPS[camera.zoomStep] ?? ZOOM_STEPS[DEFAULT_ZOOM_STEP]!;
}

/** Convert world cell coordinates to canvas (screen) pixel coordinates. */
export function worldToScreen(
  camera: Camera,
  canvasWidth: number,
  canvasHeight: number,
  worldX: number,
  worldY: number,
): { x: number; y: number } {
  const z = pxPerCell(camera);
  return {
    x: (worldX - camera.centerX) * z + canvasWidth / 2,
    y: (worldY - camera.centerY) * z + canvasHeight / 2,
  };
}

/** Convert canvas pixel coordinates to world cell coordinates. */
export function screenToWorld(
  camera: Camera,
  canvasWidth: number,
  canvasHeight: number,
  screenX: number,
  screenY: number,
): { x: number; y: number } {
  const z = pxPerCell(camera);
  return {
    x: (screenX - canvasWidth / 2) / z + camera.centerX,
    y: (screenY - canvasHeight / 2) / z + camera.centerY,
  };
}

/** Pan the camera by (dx, dy) in world-cell units. */
export function panCamera(camera: Camera, dx: number, dy: number): void {
  camera.centerX += dx;
  camera.centerY += dy;
}

/** Zoom in one step (higher zoomStep = larger px/cell). Clamps at the top step. */
export function zoomIn(camera: Camera): void {
  if (camera.zoomStep < ZOOM_STEPS.length - 1) {
    camera.zoomStep += 1;
  }
}

/** Zoom out one step. Clamps at 0. */
export function zoomOut(camera: Camera): void {
  if (camera.zoomStep > 0) {
    camera.zoomStep -= 1;
  }
}

/**
 * MFix1.2 (2026-07-16) — Zoom-to-cursor.
 *
 * Zooms one step and repositions the camera centre so the world point
 * currently under the cursor stays under the cursor after the zoom.
 *
 * Derivation: worldToScreen sets `sx = (wx - cx) * z + w/2`. Solve for
 * `cx'` such that `sx = (wx - cx') * z' + w/2` where `wx` is the world
 * point under cursor (constant across the zoom) and `z'` is the new
 * zoom scale. Result: `cx' = wx - (sx - w/2) / z'`.
 *
 * If the zoom step is already clamped (already at max or 0), this is a
 * no-op and the centre isn't touched.
 */
export function zoomInAt(
  camera: Camera,
  cursorScreenX: number,
  cursorScreenY: number,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (camera.zoomStep >= ZOOM_STEPS.length - 1) return;
  const world = screenToWorld(camera, canvasWidth, canvasHeight, cursorScreenX, cursorScreenY);
  camera.zoomStep += 1;
  const z2 = pxPerCell(camera);
  camera.centerX = world.x - (cursorScreenX - canvasWidth / 2) / z2;
  camera.centerY = world.y - (cursorScreenY - canvasHeight / 2) / z2;
}

export function zoomOutAt(
  camera: Camera,
  cursorScreenX: number,
  cursorScreenY: number,
  canvasWidth: number,
  canvasHeight: number,
): void {
  if (camera.zoomStep <= 0) return;
  const world = screenToWorld(camera, canvasWidth, canvasHeight, cursorScreenX, cursorScreenY);
  camera.zoomStep -= 1;
  const z2 = pxPerCell(camera);
  camera.centerX = world.x - (cursorScreenX - canvasWidth / 2) / z2;
  camera.centerY = world.y - (cursorScreenY - canvasHeight / 2) / z2;
}

/**
 * Clamp the camera centre to keep the map at least partially on
 * screen. Applied every frame after input to enforce v0M2.4's
 * "no pan-off-the-map" policy. Full bounds enforcement (letterboxing
 * for smaller maps) is a v0M2.4a polish candidate.
 */
export function clampCameraToMap(
  camera: Camera,
  mapWidth: number,
  mapHeight: number,
): void {
  // Allow the centre to drift out to the map edge but no further.
  camera.centerX = Math.max(0, Math.min(mapWidth, camera.centerX));
  camera.centerY = Math.max(0, Math.min(mapHeight, camera.centerY));
}
